<template>
    <div class="bsc-wrap">
        <!-- Header -->
        <div class="bsc-head">
            <div class="bsc-head-icon">
                <!-- BNB Chain logo-like icon -->
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" style="color:#F0B90B">
                    <path d="M12 2L7.5 6.5 9.5 8.5 12 6 14.5 8.5 16.5 6.5zM6 10.5L4 12.5 6 14.5 8 12.5zM9.5 15.5L12 18 14.5 15.5 12 13zM18 10.5L16 12.5 18 14.5 20 12.5zM12 10L10 12l2 2 2-2z"/>
                </svg>
            </div>
            <div>
                <h3 class="bsc-head-title">USDT BEP-20 via WalletConnect</h3>
                <div class="bsc-badges">
                    <span class="bsc-badge bsc-badge--yellow">BEP-20 Network</span>
                    <span class="bsc-badge bsc-badge--blue">USDT Only</span>
                    <span class="bsc-badge bsc-badge--green">Auto Process</span>
                </div>
            </div>
        </div>

        <!-- Notice -->
        <div class="bsc-notice">
            <p class="bsc-notice-txt">Connect your BSC wallet via WalletConnect and pay USDT directly. Your deposit is processed automatically in seconds.</p>
            <div class="bsc-warn">
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20" style="color:#fbbf24;flex-shrink:0">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
                <p><strong style="color:#fde68a">BEP-20 Only</strong> — Send only BEP-20 USDT on BSC. Sending other tokens or using a different network will result in permanent loss.</p>
            </div>
        </div>

        <!-- Amount entry -->
        <div v-if="!depositTrx" class="bsc-amount-section">
            <div class="bsc-field">
                <label class="bsc-field-lbl">Deposit Amount (USDT)</label>
                <div class="bsc-input-wrap">
                    <span class="bsc-input-pfx">$</span>
                    <input
                        v-model.number="amount"
                        type="number"
                        step="0.01"
                        :min="gatewayInfo?.min_amount || 10"
                        :max="gatewayInfo?.max_amount || 50000"
                        class="bsc-input"
                        placeholder="Enter USDT amount"
                        :disabled="step !== 'idle'"
                    />
                </div>
                <p class="bsc-field-hint" v-if="gatewayInfo">
                    Min: ${{ gatewayInfo.min_amount }} — Max: ${{ gatewayInfo.max_amount }}
                </p>
            </div>

            <!-- Summary -->
            <div v-if="amount > 0 && gatewayInfo" class="bsc-summary">
                <div class="bsc-sum-row">
                    <span class="bsc-sum-lbl">You send</span>
                    <span class="bsc-sum-val">{{ totalAmount.toFixed(2) }} USDT</span>
                </div>
                <div v-if="gatewayInfo.fixed_charge > 0 || gatewayInfo.percent_charge > 0" class="bsc-sum-row">
                    <span class="bsc-sum-lbl">Fee</span>
                    <span class="bsc-sum-val" style="color:#fbbf24">{{ charge.toFixed(2) }} USDT</span>
                </div>
                <div class="bsc-sum-row bsc-sum-total">
                    <span class="bsc-sum-lbl" style="font-weight:700;color:#e2e8f0">You receive</span>
                    <span class="bsc-sum-val" style="color:#10b981;font-weight:800">${{ (parseFloat(amount) || 0).toFixed(2) }}</span>
                </div>
            </div>
        </div>

        <!-- Selected wallet (not yet connected) -->
        <div v-else-if="selectedWalletId && walletAddresses.length > 0" class="bsc-selected-bar">
            <div class="bsc-selected-info">
                <span class="bsc-selected-dot"></span>
                <div class="bsc-selected-addr-wrap">
                    <span class="bsc-selected-label">
                        Selected Wallet — {{ selectedWalletLabel }}
                    </span>
                    <span class="bsc-selected-addr">{{ walletAddresses.find(w => w.id === selectedWalletId)?.address }}</span>
                    <span style="color:#f59e0b;font-size:10px;font-weight:600;background:rgba(245,158,11,0.1);padding:3px 8px;border-radius:4px;margin-top:6px;display:inline-block">
                        Click "Pay Now" to connect
                    </span>
                </div>
                <span class="bsc-badge bsc-badge--yellow" style="font-size:10px">Ready</span>
            </div>
        </div>

        <!-- ── STEP: idle → wallet picker + pay ── -->
        <div v-if="step === 'idle' && !depositTrx" class="bsc-action-area">

            <!-- Address book selector -->
            <div v-if="walletAddresses.length > 0" class="bsc-picker">
                <div class="bsc-picker-head">
                    <svg width="13" height="13" fill="currentColor" viewBox="0 0 20 20" style="color:#a75ded;flex-shrink:0"><path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 010 2H5a1 1 0 01-1-1zm5-1a1 1 0 000 2h1a1 1 0 000-2H9z"/></svg>
                    <span class="bsc-picker-title">Pay from saved wallet</span>
                </div>
                <div class="bsc-wallet-list">
                    <div
                        v-for="w in walletAddresses"
                        :key="w.id"
                        class="bsc-wallet-item"
                        :class="selectedWalletId === w.id ? 'bsc-wallet-item--active' : ''"
                        @click="selectWallet(w)"
                    >
                        <div class="bsc-wallet-radio">
                            <div v-if="selectedWalletId === w.id" class="bsc-wallet-radio-dot"></div>
                        </div>
                        <div class="bsc-wallet-meta">
                            <span class="bsc-wallet-label">{{ w.label }}</span>
                            <span class="bsc-wallet-addr">{{ w.truncated }}</span>
                        </div>
                        <span v-if="w.is_primary" class="bsc-badge bsc-badge--green" style="font-size:9px;padding:2px 7px">Primary</span>
                    </div>
                </div>
                <p class="bsc-picker-hint">
                    <a href="/user/settings/wallets" class="bsc-picker-link">
                        <svg width="11" height="11" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clip-rule="evenodd"/></svg>
                        Manage / add wallet addresses
                    </a>
                </p>
            </div>

            <!-- No saved addresses nudge -->
            <div v-else class="bsc-no-wallets">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20" style="color:#a75ded;flex-shrink:0"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                <span>
                    No saved BSC addresses.
                    <a href="/user/settings/wallets" class="bsc-picker-link">Add one in your wallet address book</a>
                    or connect your wallet below.
                </span>
            </div>

            <!-- Connected bar (shown after wallet is selected/linked) -->
            <div v-if="connectedAddress" class="bsc-connected-bar">
                <div class="bsc-connected-info">
                    <span class="bsc-connected-dot" :style="{ backgroundColor: connectedProvider ? '#10b981' : '#f59e0b' }"></span>
                    <div class="bsc-connected-addr-wrap">
                        <span class="bsc-connected-label">
                            {{ connectedProvider ? 'Connected Wallet' : 'Selected Wallet' }}
                            <span v-if="selectedWalletLabel" style="color:rgba(255,255,255,0.5)"> — {{ selectedWalletLabel }}</span>
                        </span>
                        <span class="bsc-connected-addr">{{ connectedAddress }}</span>
                        <div style="display:flex;gap:8px;margin-top:6px;align-items:center;flex-wrap:wrap">
                            <span v-if="!connectedProvider" style="color:#f59e0b;font-size:10px;font-weight:600;background:rgba(245,158,11,0.1);padding:2px 6px;border-radius:4px">
                                Click "Pay Now" to connect
                            </span>
                            <small v-if="connectedProvider" style="color:rgba(148,163,184,0.6);font-size:11px">
                                Network: <strong style="color:#fff">{{ connectedChainId === 56 ? 'BSC' : (connectedChainId ? 'Chain ' + connectedChainId : 'Fetching…') }}</strong>
                            </small>
                            <small v-if="connectedProvider && usdtBalance !== null" style="color:rgba(148,163,184,0.6);font-size:11px">
                                USDT Balance: <strong style="color:#fff">{{ usdtBalance.toFixed(6) }}</strong>
                            </small>
                            <small v-if="connectedProvider && usdtBalance === null" style="color:rgba(148,163,184,0.4);font-size:11px">
                                Fetching balance…
                            </small>
                        </div>
                    </div>
                    <span class="bsc-badge bsc-badge--green" style="font-size:10px">BSC</span>
                </div>
                <button class="bsc-btn bsc-btn--disconnect" @click="disconnectWallet">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                </button>
            </div>

            <!-- Single Pay Now button -->
            <button
                class="bsc-btn bsc-btn--pay"
                :disabled="!amount || amount <= 0"
                @click="payNow"
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
                </svg>
                Pay Now — {{ totalAmount.toFixed(2) }} USDT
            </button>
        </div>

        <!-- ── STEP: connecting ── -->
        <div v-if="step === 'connecting'" class="bsc-status bsc-status--info">
            <div class="bsc-spin-ring"></div>
            <div>
                <p class="bsc-status-title">Connecting Wallet…</p>
                <p class="bsc-status-sub">Please approve the connection in your wallet app.</p>
            </div>
        </div>

        <!-- ── STEP: switching network ── -->
        <div v-if="step === 'switching'" class="bsc-status bsc-status--info">
            <div class="bsc-spin-ring"></div>
            <div>
                <p class="bsc-status-title">Switching to BSC…</p>
                <p class="bsc-status-sub">Please confirm the network switch in your wallet.</p>
            </div>
        </div>

        <!-- ── STEP: initiating (backend call) ── -->
        <div v-if="step === 'initiating'" class="bsc-status bsc-status--info">
            <div class="bsc-spin-ring"></div>
            <div>
                <p class="bsc-status-title">Creating Deposit Record…</p>
                <p class="bsc-status-sub">Setting up your deposit on the server.</p>
            </div>
        </div>

        <!-- ── STEP: approving (wallet tx) ── -->
        <div v-if="step === 'approving'" class="bsc-status bsc-status--warning">
            <div class="bsc-spin-ring bsc-spin-ring--yellow"></div>
            <div>
                <p class="bsc-status-title">Approve Transaction in Wallet</p>
                <p class="bsc-status-sub">Check your wallet — approve the USDT transfer of <strong>{{ totalAmount.toFixed(2) }} USDT</strong>.</p>
            </div>
        </div>

        <!-- ── STEP: broadcasting ── -->
        <div v-if="step === 'broadcasting'" class="bsc-status bsc-status--info">
            <div class="bsc-spin-ring"></div>
            <div>
                <p class="bsc-status-title">Broadcasting Transaction…</p>
                <p class="bsc-status-sub">Your transaction is being submitted to BSC.</p>
            </div>
        </div>

        <!-- ── STEP: verifying ── -->
        <div v-if="step === 'verifying'" class="bsc-status bsc-status--info">
            <div class="bsc-spin-ring"></div>
            <div>
                <p class="bsc-status-title">Verifying on Blockchain…</p>
                <p class="bsc-status-sub">Confirming your USDT transfer on BSC. This takes a few seconds.</p>
                <p v-if="txHash" class="bsc-txhash">
                    TX: <a :href="`https://bscscan.com/tx/${txHash}`" target="_blank" rel="noopener" class="bsc-txhash-link">{{ shortTx(txHash) }}</a>
                </p>
            </div>
        </div>

        <!-- ── STEP: success ── -->
        <div v-if="step === 'success'" class="bsc-status bsc-status--success">
            <div class="bsc-check-icon">
                <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" style="color:#10b981">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
                </svg>
            </div>
            <div>
                <p class="bsc-status-title" style="color:#10b981">Deposit Confirmed!</p>
                <p class="bsc-status-sub">{{ successMessage }}</p>
                <p v-if="txHash" class="bsc-txhash">
                    TX: <a :href="`https://bscscan.com/tx/${txHash}`" target="_blank" rel="noopener" class="bsc-txhash-link">{{ shortTx(txHash) }}</a>
                </p>
            </div>
        </div>

        <!-- ── STEP: error ── -->
        <div v-if="step === 'error'" class="bsc-status bsc-status--error">
            <svg width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="color:#f87171;flex-shrink:0">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <div>
                <p class="bsc-status-title" style="color:#f87171">Payment Failed</p>
                <p class="bsc-status-sub" style="white-space:pre-wrap">{{ errorMessage }}</p>
                <div style="display:flex;gap:8px;margin-top:10px;flex-wrap:wrap">
                    <button class="bsc-btn bsc-btn--ghost" @click="reset">Try Again</button>
                    <button
                        v-if="errorMessage && errorMessage.toLowerCase().includes('Chain ID')"
                        class="bsc-btn bsc-btn--primary"
                        @click="attemptSwitchFromError"
                    >
                        Switch to BSC
                    </button>
                    <button
                        v-if="errorMessage && errorMessage.toLowerCase().includes('wrong network')"
                        class="bsc-btn bsc-btn--primary"
                        @click="attemptSwitchFromError"
                    >
                        Switch to BSC
                    </button>
                    <button
                        v-if="errorMessage && (errorMessage.toLowerCase().includes('bsc') || errorMessage.toLowerCase().includes('switch'))"
                        class="bsc-btn bsc-btn--primary"
                        @click="attemptSwitchFromError"
                    >
                        Switch Wallet to BSC
                    </button>
                </div>
            </div>
        </div>

        <!-- Feature pills -->
        <div class="bsc-features">
            <div class="bsc-feat">
                <div class="bsc-feat-ic" style="background:rgba(16,185,129,0.18);color:#10b981">
                    <svg width="13" height="13" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                </div>
                <div><p class="bsc-feat-title" style="color:#10b981">Auto Credit</p><p class="bsc-feat-sub">Instant verification</p></div>
            </div>
            <div class="bsc-feat">
                <div class="bsc-feat-ic" style="background:rgba(96,165,250,0.18);color:#60a5fa">
                    <svg width="13" height="13" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                </div>
                <div><p class="bsc-feat-title" style="color:#60a5fa">Secure</p><p class="bsc-feat-sub">WalletConnect encrypted</p></div>
            </div>
            <div class="bsc-feat">
                <div class="bsc-feat-ic" style="background:rgba(251,191,36,0.18);color:#fbbf24">
                    <svg width="13" height="13" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clip-rule="evenodd"/></svg>
                </div>
                <div><p class="bsc-feat-title" style="color:#fbbf24">Low Fees</p><p class="bsc-feat-sub">BSC gas is cheap</p></div>
            </div>
            <div class="bsc-feat">
                <div class="bsc-feat-ic" style="background:rgba(167,93,237,0.18);color:#a75ded">
                    <svg width="13" height="13" fill="currentColor" viewBox="0 0 20 20"><path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"/></svg>
                </div>
                <div><p class="bsc-feat-title" style="color:#a75ded">Any Wallet</p><p class="bsc-feat-sub">MetaMask, Trust &amp; more</p></div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { BrowserProvider, Contract, parseUnits, formatUnits, Interface } from 'ethers'
import { getAppKit, openWcModal } from '@/utils/appkit.js'
import axios from 'axios'

const props = defineProps({
    themeColors:     { type: Object, default: () => ({ primary: '#a75ded', primaryRgb: '167,93,237' }) },
    walletAddresses: { type: Array,  default: () => [] },
})

const emit = defineEmits(['depositSuccess'])

// ── State ──────────────────────────────────────────────────────────────────
const amount             = ref(0)
const step               = ref('idle')
const errorMessage       = ref('')
const successMessage     = ref('')
const txHash             = ref('')
const depositTrx         = ref('')
const connectedAddress   = ref('')
const connectedProvider  = ref(null)  // stores active EIP-1193 provider
const connectedChainId    = ref(null)
const usdtBalance         = ref(null)
const gatewayInfo        = ref(null)
const selectedWalletId   = ref(null)  // ID of chosen address-book wallet

// BSC chain config
const BSC_CHAIN_ID_HEX = '0x38'
const BSC_CHAIN_PARAMS = {
    chainId: BSC_CHAIN_ID_HEX,
    chainName: 'BNB Smart Chain',
    nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
    rpcUrls: ['https://bsc-dataseed.binance.org'],
    blockExplorerUrls: ['https://bscscan.com'],
}

const ERC20_ABI = [
    'function transfer(address to, uint256 amount) returns (bool)',
    'function allowance(address owner, address spender) view returns (uint256)',
    'function balanceOf(address account) view returns (uint256)',
]

// ── Computed ───────────────────────────────────────────────────────────────
const charge = computed(() => {
    const amt = parseFloat(amount.value) || 0
    if (!gatewayInfo.value || amt <= 0) return 0
    const fixed = parseFloat(gatewayInfo.value.fixed_charge) || 0
    const pct   = parseFloat(gatewayInfo.value.percent_charge) || 0
    return fixed + (amt * pct / 100)
})

const totalAmount = computed(() => {
    const amt = parseFloat(amount.value) || 0
    return amt + charge.value
})

const isWithinGatewayLimits = computed(() => {
    const amt = parseFloat(amount.value) || 0
    const min = parseFloat(gatewayInfo.value?.min_amount || 10)
    const max = parseFloat(gatewayInfo.value?.max_amount || 50000)
    if (amt <= 0) return false
    return amt >= min && amt <= max
})

const selectedWalletLabel = computed(() => {
    if (!selectedWalletId.value) return ''
    const wallet = props.walletAddresses.find(w => w.id === selectedWalletId.value)
    return wallet?.label || ''
})

// ── Helpers ────────────────────────────────────────────────────────────────
const shortAddr = (addr) => addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : ''
const shortTx   = (tx)   => tx   ? `${tx.slice(0, 10)}…${tx.slice(-8)}`   : ''

const setError = (msg) => {
    step.value = 'error'
    errorMessage.value = msg
}

const reset = () => {
    step.value              = 'idle'
    errorMessage.value      = ''
    successMessage.value    = ''
    txHash.value            = ''
    depositTrx.value        = ''
    // Keep wallet connected so user can pay again without reconnecting
    // connectedAddress and connectedProvider stay set
}

const refreshBalance = async () => {
    if (connectedProvider.value && connectedAddress.value) {
        await fetchUsdtBalance(connectedProvider.value)
    }
}

// ── Load gateway info ──────────────────────────────────────────────────────
const loadGatewayInfo = async () => {
    try {
        const { data } = await axios.get('/user/bsc/gateway-info')
        if (data.success) gatewayInfo.value = data.data
    } catch {
        // gateway info optional; limits validated on backend
    }
}

onMounted(async () => {
    loadGatewayInfo()
    const primary = props.walletAddresses.find(w => w.is_primary) ?? props.walletAddresses[0]
    if (primary) selectedWalletId.value = primary.id

    // Silently pre-connect: if MetaMask already has this site approved (no popup),
    // populate state now so "Pay Now" goes straight to payment without a connect step.
    if (typeof window.ethereum !== 'undefined') {
        try {
            const injected = window.ethereum.providers?.find?.(p => p.isMetaMask) ?? window.ethereum
            const accounts  = await injected.request({ method: 'eth_accounts' }).catch(() => [])
            if (accounts?.length) {
                const savedAddrs = props.walletAddresses.map(w => w.address.toLowerCase())
                const matched    = accounts.find(a => savedAddrs.includes(a.toLowerCase()))
                // Only auto-connect if MetaMask has an address that's actually in the saved list.
                // Never fall back to accounts[0] — that could pre-connect the wrong wallet.
                if (matched) {
                    const useAddr = matched.toLowerCase()
                    connectedAddress.value  = useAddr
                    connectedProvider.value = injected
                    const found = props.walletAddresses.find(w => w.address.toLowerCase() === useAddr)
                    if (found) selectedWalletId.value = found.id
                }
            }
        } catch {
            // Silent failure — manual connection via Pay Now still works
        }
    }
})

// Fetch USDT balance and chain when connectedAddress/provider or gatewayInfo change
const fetchUsdtBalance = async (provider) => {
    usdtBalance.value = null
    connectedChainId.value = null
    try {
        if (!provider || !gatewayInfo.value || !connectedAddress.value) return
        const readProvider = new BrowserProvider(provider)
        const net = await readProvider.getNetwork()
        // Normalize chainId to a Number so downstream === checks work reliably
        connectedChainId.value = net?.chainId !== undefined && net?.chainId !== null ? Number(net.chainId) : null
        const usdtContract = new Contract(gatewayInfo.value.usdt_contract, ERC20_ABI, readProvider)
        const raw = await usdtContract.balanceOf(connectedAddress.value)
        const value = typeof raw === 'bigint' ? raw : BigInt(raw.toString())
        usdtBalance.value = parseFloat(formatUnits(value, 18))
    } catch (e) {
        // non-fatal; keep null to indicate unknown
        usdtBalance.value = null
    }
}

watch([() => connectedAddress.value, () => connectedProvider.value, () => gatewayInfo.value], async ([addr, prov, gw]) => {
    if (!addr || !prov) return
    await fetchUsdtBalance(prov)
})

// Try to reuse an existing WalletConnect/AppKit session on mount so users
// who previously approved the site don't need to reconnect.
onMounted(async () => {
    try {
        const modal = getAppKit()
        const addr = modal.getAddress?.()
        if (addr) {
            // Prefer the appkit wallet provider, fall back to injected provider
            const p = (modal.getWalletProvider && modal.getWalletProvider()) || window.ethereum || null
            if (p) {
                connectedAddress.value = String(addr).toLowerCase()
                connectedProvider.value = p
                const found = props.walletAddresses.find(w => w.address && w.address.toLowerCase() === String(addr).toLowerCase())
                if (found) selectedWalletId.value = found.id
            }
        }
    } catch (e) {
        // Non-fatal: if AppKit isn't configured or lookup fails, ignore.
    }
})

const selectWallet = async (w) => {
    const selAddr = w.address?.toLowerCase?.() ?? null

    // Already connected to this exact wallet — nothing to do
    if (selectedWalletId.value === w.id && connectedAddress.value?.toLowerCase() === selAddr && connectedProvider.value) {
        return
    }

    selectedWalletId.value = w.id

    // Already connected to the right address under a different label — just update selection
    if (selAddr && connectedAddress.value?.toLowerCase() === selAddr && connectedProvider.value) {
        return
    }

    // Disconnect and clear the old wallet
    if (connectedProvider.value || connectedAddress.value) {
        try { const m = getAppKit(); if (m?.disconnect) await m.disconnect() } catch {}
        connectedAddress.value  = ''
        connectedProvider.value = null
        connectedChainId.value  = null
        usdtBalance.value       = null
    }

    if (!selAddr) return

    // Try MetaMask silently first (no popup) — only use if it has the exact selected address
    try {
        if (typeof window.ethereum !== 'undefined') {
            const injected = window.ethereum.providers?.find?.(p => p.isMetaMask)
                ?? (window.ethereum.isMetaMask ? window.ethereum : null)
            if (injected) {
                const accounts = await injected.request({ method: 'eth_accounts' }).catch(() => [])
                const match = accounts?.find(a => a.toLowerCase() === selAddr)
                if (match) {
                    connectedAddress.value  = match.toLowerCase()
                    connectedProvider.value = injected
                    await fetchUsdtBalance(injected)
                    return  // MetaMask had this wallet — done
                }
            }
        }
    } catch {}

    // MetaMask doesn't have this wallet — open WalletConnect immediately so the
    // user can connect the selected wallet right now (wallet switch UX)
    try {
        step.value = 'connecting'
        const { provider: wcProvider, address: wcAddress } = await openWcModal()
        step.value = 'idle'
        const wcAddrLower = wcAddress.toLowerCase()
        connectedAddress.value  = wcAddrLower
        connectedProvider.value = wcProvider
        // If user connected a different address than selected, auto-match it to the address book
        if (wcAddrLower !== selAddr) {
            const bookMatch = props.walletAddresses.find(wa => wa.address?.toLowerCase() === wcAddrLower)
            if (bookMatch) selectedWalletId.value = bookMatch.id
        }
        await fetchUsdtBalance(wcProvider)
    } catch (err) {
        step.value = 'idle'
        // User cancelled WalletConnect — wallet stays selected but not yet connected
    }
}

// Smart auto-connect: try all available providers to find the selected address
const autoConnectWallet = async (wallet) => {
    if (!wallet?.address) return
    
    const expectedAddr = wallet.address.toLowerCase()
    
    try {
        // Try MetaMask / browser extension first
        if (typeof window.ethereum !== 'undefined') {
            try {
                // Try direct MetaMask detection
                const metaMaskProvider = window.ethereum.providers?.find?.(p => p.isMetaMask)
                    ?? (window.ethereum.isMetaMask ? window.ethereum : null)
                
                if (metaMaskProvider) {
                    const accounts = await metaMaskProvider.request({ 
                        method: 'eth_accounts' 
                    }).catch(() => [])
                    
                    if (accounts?.length) {
                        const match = accounts.find(a => a.toLowerCase() === expectedAddr)
                        if (match) {
                            connectedAddress.value  = match.toLowerCase()
                            connectedProvider.value = metaMaskProvider
                            await fetchUsdtBalance(metaMaskProvider)
                            return
                        }
                    }
                }
                
                // If MetaMask didn't have it, try the default ethereum provider
                const defaultProvider = window.ethereum
                if (defaultProvider && defaultProvider !== metaMaskProvider) {
                    const accounts = await defaultProvider.request({ 
                        method: 'eth_accounts' 
                    }).catch(() => [])
                    
                    if (accounts?.length) {
                        const match = accounts.find(a => a.toLowerCase() === expectedAddr)
                        if (match) {
                            connectedAddress.value  = match.toLowerCase()
                            connectedProvider.value = defaultProvider
                            await fetchUsdtBalance(defaultProvider)
                            return
                        }
                    }
                }
            } catch (extErr) {
                // Silent fail — will try AppKit next
            }
        }

        // Try AppKit/WalletConnect session
        try {
            const modal = getAppKit()
            const appKitAddr = modal.getAddress?.()
            if (appKitAddr?.toLowerCase() === expectedAddr) {
                const provider = modal.getWalletProvider?.() || window.ethereum
                if (provider) {
                    connectedAddress.value  = appKitAddr.toLowerCase()
                    connectedProvider.value = provider
                    await fetchUsdtBalance(provider)
                    return
                }
            }
        } catch (e) {
            // AppKit not available or no matching address
        }
        
        // If auto-connect fails, wallet just shows as "Selected" (not connected)
        // User can click "Pay Now" to manually connect
        
    } catch (err) {
        // Non-fatal: auto-connect failed, user can still connect manually
        console.warn('[BscUsdtPayment] Auto-connect failed for wallet', expectedAddr, err)
    }
}

// ── Switch / add BSC network ───────────────────────────────────────────────
const ensureBscNetwork = async (provider) => {
    try {
        await provider.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: BSC_CHAIN_ID_HEX }],
        })
        return
    } catch (switchErr) {
        // If chain not found, attempt to add then switch
        if (switchErr && (switchErr.code === 4902 || /Unrecognized chain/i.test(switchErr.message || ''))) {
            await provider.request({
                method: 'wallet_addEthereumChain',
                params: [BSC_CHAIN_PARAMS],
            })
            // After adding, try to switch again
            await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: BSC_CHAIN_ID_HEX }] })
            return
        }
        if (switchErr && (switchErr.code === 4001 || /user rejected/i.test(switchErr.message || ''))) {
            // User explicitly rejected the switch
            throw switchErr
        }
        // Some providers (mobile WalletConnect) may not support programmatic network switching.
        // In that case, check the current chainId; if it's already BSC, return; otherwise signal inability.
        try {
            const current = await provider.request({ method: 'eth_chainId' })
            if (String(current).toLowerCase() === String(BSC_CHAIN_ID_HEX).toLowerCase()) return
        } catch (_) {
            // fallthrough
        }
        const e = new Error('Unable to switch wallet network programmatically')
        e.code = 'UNABLE_TO_SWITCH'
        throw e
    }
}

// Attempt switch from the error UI and resume payment if successful
const attemptSwitchFromError = async () => {
    if (!connectedProvider.value) {
        setError('No connected wallet to switch. Please connect your wallet first.')
        return
    }
    try {
        step.value = 'switching'
        await ensureBscNetwork(connectedProvider.value)
        // Refresh balance and chain state
        await fetchUsdtBalance(connectedProvider.value)
        // Retry payment flow
        await initiateAndPay()
    } catch (err) {
        if (err.code === 4001) {
            setError('Network switch cancelled by user. Please switch your wallet to BSC and try again.')
        } else if (err.code === 'UNABLE_TO_SWITCH') {
            setError('This wallet does not support programmatic network switching. Please switch your wallet to BNB Smart Chain (BSC) and click Try Again.')
        } else {
            setError(err.message || 'Failed to switch network')
        }
    }
}

const disconnectWallet = async () => {
    connectedAddress.value  = ''
    connectedProvider.value = null
    step.value = 'idle'
    try { const m = getAppKit(); if (m?.disconnect) await m.disconnect() } catch {}
}

// ── Pay Now: connect wallet (if needed) then immediately pay ───────────────
const payNow = async () => {
    if (!amount.value || amount.value <= 0) return

    if (!gatewayInfo.value) {
        setError('Gateway details are still loading. Please wait a moment and try again.')
        return
    }

    if (!isWithinGatewayLimits.value) {
        const min = parseFloat(gatewayInfo.value.min_amount || 10)
        const max = parseFloat(gatewayInfo.value.max_amount || 50000)
        setError(`Amount must be between ${min} and ${max} USDT.`)
        return
    }

    // Determine expected sender address from address book selection
    const selected = props.walletAddresses.find(w => w.id === selectedWalletId.value)
    const expectedAddr = selected?.address?.toLowerCase() ?? null
    
    if (!expectedAddr) {
        setError('No wallet selected. Please select a wallet from the list.')
        return
    }
    
    // Already have a live provider for the correct address — go straight to payment
    if (connectedAddress.value && connectedProvider.value && connectedAddress.value.toLowerCase() === expectedAddr) {
        await initiateAndPay()
        return
    }
    
    // User selected a different wallet than the one connected — disconnect and reconnect
    if (connectedAddress.value?.toLowerCase() !== expectedAddr) {
        connectedAddress.value  = ''
        connectedProvider.value = null
        connectedChainId.value  = null
        usdtBalance.value       = null
    }

    // If we have a selected wallet but no provider, try to connect it
    if (!connectedProvider.value) {
        try {
            step.value = 'connecting'
            let connected = false
            
            // Try MetaMask first
            if (typeof window.ethereum !== 'undefined' && !connected) {
                try {
                    const injected = window.ethereum.providers?.find?.(p => p.isMetaMask)
                        ?? (window.ethereum.isMetaMask ? window.ethereum : null)
                    
                    if (injected) {
                        const accounts = await injected.request({ method: 'eth_accounts' }).catch(() => [])
                        const match = accounts?.find(a => a.toLowerCase() === expectedAddr)
                        if (match) {
                            connectedAddress.value = match.toLowerCase()
                            connectedProvider.value = injected
                            connected = true
                        }
                    }
                } catch (extErr) {
                    if (extErr.code === 4001) throw extErr
                }
            }
            
            // Try AppKit/WalletConnect
            if (!connected) {
                try {
                    const modal = getAppKit()
                    const appKitAddr = modal.getAddress?.()
                    if (appKitAddr?.toLowerCase() === expectedAddr) {
                        const provider = modal.getWalletProvider?.() || window.ethereum
                        if (provider) {
                            connectedAddress.value = appKitAddr.toLowerCase()
                            connectedProvider.value = provider
                            connected = true
                        }
                    }
                } catch (e) {
                    // AppKit not available
                }
            }
            
            // If we found a provider with the selected wallet, use it
            if (connected) {
                await initiateAndPay()
                return
            }

            // No existing provider found for the selected address
            // Proceed to request connection and verify it's the right wallet
            connectedAddress.value = ''
            connectedProvider.value = null
        } catch (err) {
            if (err.code !== 4001) {
                step.value = 'idle'
            }
            throw err
        }
    }

    try {
        step.value = 'connecting'

        // ── Path A: Browser extension (MetaMask, Trust, OKX…) ──────────────
        if (typeof window.ethereum !== 'undefined') {
            try {
                const injected = window.ethereum.providers?.find?.(p => p.isMetaMask)
                    ?? (window.ethereum.isMetaMask ? window.ethereum : null)

                if (injected) {
                    const accounts = await injected.request({ method: 'eth_accounts' }).catch(() => [])
                    const match = accounts?.find(a => a.toLowerCase() === expectedAddr)

                    if (match) {
                        // MetaMask has exactly the selected wallet — use it directly, no popup needed
                        connectedAddress.value  = match.toLowerCase()
                        connectedProvider.value = injected
                        await initiateAndPay()
                        return
                    }
                    // MetaMask is connected but with a different account (e.g. "My Wallet" is active
                    // but user selected "Tron link"). Do NOT use the wrong account and do NOT
                    // call eth_requestAccounts (that would pop up MetaMask with the wrong wallet).
                    // Fall through silently to WalletConnect so the user can connect the right wallet.
                }
            } catch (extErr) {
                if (extErr.code === 4001) throw extErr
            }
        }

        // ── Path B: WalletConnect modal ──────────────────────────────────────
        const { provider: wcProvider, address: wcAddress } = await openWcModal()
        const wcAddrLower = wcAddress.toLowerCase()

        // Accept whatever address WalletConnect connected — the backend only
        // verifies USDT reached the platform wallet, not which address sent it.
        // If the connected address is in the user's verified address book,
        // auto-select it so the UI stays consistent.
        connectedAddress.value  = wcAddrLower
        connectedProvider.value = wcProvider

        const bookMatch = props.walletAddresses.find(w => w.address?.toLowerCase() === wcAddrLower)
        if (bookMatch) {
            selectedWalletId.value = bookMatch.id
        }

        await initiateAndPay()

    } catch (err) {
        const msg = err.message || ''
        if (err.code === 4001 || msg.toLowerCase().includes('rejected') || msg.toLowerCase().includes('cancelled')) {
            setError('Wallet connection cancelled. Please try again.')
        } else if (msg.includes('not configured') || msg.includes('WalletConnect is not')) {
            setError('WalletConnect is not configured. Please contact support.')
        } else {
            setError(msg || 'Could not connect wallet. Please try again or use MetaMask.')
        }
    }
}

// ── Execute payment (called from payNow) ──────────────────────────────────
const initiateAndPay = async (rawProvider) => {
    // When called via template @click, rawProvider is a MouseEvent — use stored provider
    const provider = (rawProvider instanceof Event || !rawProvider)
        ? connectedProvider.value
        : rawProvider

    try {
        if (!connectedAddress.value) {
            throw new Error('No wallet connected. Please click "Pay Now" and approve the connection.')
        }
        if (!provider) {
            throw new Error('Wallet session lost. Please disconnect and reconnect your wallet.')
        }

        // Re-confirm BSC network before sending
        step.value = 'switching'
        await ensureBscNetwork(provider)

        // Create backend deposit record
        step.value = 'initiating'
        const initRes = await axios.post('/user/bsc/initiate-deposit', {
            amount:         Number(amount.value),
            wallet_address: connectedAddress.value.trim().toLowerCase(),
        })

        if (!initRes.data.success) {
            throw new Error(initRes.data.message || 'Failed to initiate deposit')
        }

        const { trx, platform_wallet, usdt_contract, amount: sendAmount } = initRes.data
        depositTrx.value = trx

        // Send USDT transfer via ethers.js
        step.value = 'approving'
        const ethersProvider = new BrowserProvider(provider)
        // Pass the known address so ethers never calls eth_requestAccounts internally
        const signer         = await ethersProvider.getSigner(connectedAddress.value)
        if (!usdt_contract) {
            throw new Error('USDT contract address is not configured. Cannot perform transfer.')
        }
        const usdtContract = new Contract(usdt_contract, ERC20_ABI, signer)

        // Prepare an ERC20 Interface for calldata encoding fallback (safe even if Contract is malformed)
        const erc20Interface = new Interface(ERC20_ABI)

        // Defensive checks: ensure contract instance and the transfer method exist
        if (import.meta.env.DEV) {
            try {
                console.debug('[BscUsdtPayment] usdtContract prepared', {
                    usdt_contract,
                    hasInterface: !!usdtContract.interface,
                    transferType: typeof usdtContract.transfer,
                    signer: !!signer
                })
            } catch (dbgErr) {
                console.debug('[BscUsdtPayment] debug error reading usdtContract', dbgErr)
            }
        }

        if (!usdtContract || typeof usdtContract.transfer !== 'function') {
            if (import.meta.env.DEV) console.error('[BscUsdtPayment] Invalid USDT contract instance', { usdt_contract, usdtContract, signer })
            // We'll attempt fallback below, but surface clear message if fallback not possible
        }

        const amountInWei = parseUnits(sendAmount.toString(), 18)

        // Pre-flight checks: ensure provider network is BSC and user has enough USDT
        try {
            const net = await ethersProvider.getNetwork()
            if (Number(net?.chainId) !== 56) {
                // If ensureBscNetwork couldn't force a switch (mobile wallets), surface meaningful error
                throw new Error(
                    `Your wallet is connected to the wrong network (Chain ID: ${net?.chainId || 'unknown'}). ` +
                    'Please switch to BNB Smart Chain (BSC, Chain ID: 56) and try again.'
                )
            }
        } catch (netErr) {
            // If it's a custom error we just threw, re-throw it as is
            if (netErr.message && netErr.message.includes('wrong network')) {
                throw netErr
            }
            // Fall back to a helpful message
            throw new Error(netErr.message || 'Unable to determine wallet network. Ensure you are connected to BSC (Chain ID: 56).')
        }

        // Check USDT balance before attempting transfer
        try {
            const rawBalance = await usdtContract.balanceOf(connectedAddress.value)
            const balanceBig = typeof rawBalance === 'bigint' ? rawBalance : BigInt(rawBalance.toString())
            const displayBalance = parseFloat(formatUnits(balanceBig, 18))

            // rawBalance and amountInWei are BigInt (ethers v6). Use comparison accordingly.
            if (balanceBig < amountInWei) {
                const required = parseFloat(formatUnits(amountInWei, 18))
                throw new Error(
                    `Insufficient USDT balance. You have ${displayBalance.toFixed(6)} USDT but need ${required.toFixed(6)} USDT for this deposit.`
                )
            }
        } catch (balErr) {
            // If balanceOf fails, surface a clear message
            if (balErr.message && balErr.message.toLowerCase().includes('call exception')) {
                throw new Error(
                    'Unable to read USDT balance from wallet. Please ensure:\n' +
                    '1. Your wallet is connected to BSC (BNB Smart Chain)\n' +
                    '2. Your wallet is unlocked\n' +
                    '3. USDT is available on BSC network'
                )
            }
            // Check if this is the balance insufficiency error we just threw
            if (balErr.message && balErr.message.includes('Insufficient USDT')) {
                throw balErr
            }
            // Generic fallback
            throw new Error(balErr.message || 'Unable to verify wallet balance. Please check your wallet is on BSC.')
        }

        // Try a simulation / estimate to capture revert reasons early
        try {
            // ethers v6 exposes these helpers on the function itself, not on contract.estimateGas/callStatic
            try {
                await usdtContract.transfer.estimateGas(platform_wallet, amountInWei)
            } catch (estErr) {
                // attempt static call to get revert reason from the contract
                try {
                    await usdtContract.transfer.staticCall(platform_wallet, amountInWei)
                } catch (simErr) {
                    throw new Error(simErr?.reason || simErr?.message || 'Token transfer simulation failed')
                }
            }
        } catch (preflightErr) {
            // Bubble a helpful message to the UI
            throw new Error(preflightErr.message || 'Preflight transfer check failed')
        }

        let tx
        try {
            if (typeof usdtContract.transfer === 'function') {
                if (import.meta.env.DEV) console.debug('[BscUsdtPayment] invoking usdtContract.transfer', { transferType: typeof usdtContract.transfer, usdtContract })
                tx = await usdtContract.transfer(platform_wallet, amountInWei)
            } else {
                // Fallback for providers where Contract write methods are not functions
                // (some WalletConnect providers). Build calldata and use eth_sendTransaction.
                try {
                    // Prefer contract.interface when available, else use standalone Interface
                    const data = (usdtContract && usdtContract.interface)
                        ? usdtContract.interface.encodeFunctionData('transfer', [platform_wallet, amountInWei])
                        : erc20Interface.encodeFunctionData('transfer', [platform_wallet, amountInWei])
                    const from = connectedAddress.value
                    const to   = usdt_contract
                    const params = [{ from, to, data }]
                    // provider may be an EIP-1193 provider with request
                    if (typeof provider.request === 'function') {
                        const txHashFallback = await provider.request({ method: 'eth_sendTransaction', params })
                        // Wrap minimal tx object to keep waiting semantics consistent
                        tx = { hash: txHashFallback, wait: async (confirms = 1) => {
                            // simple wait: poll for receipt via ethers Provider
                            const rpcProvider = new BrowserProvider(provider)
                            let receipt = null
                            for (let i = 0; i < 60; i++) {
                                try { receipt = await rpcProvider.getTransactionReceipt(txHashFallback) } catch(e){}
                                if (receipt && receipt.confirmations >= confirms) return receipt
                                await new Promise(r => setTimeout(r, 1000))
                            }
                            throw new Error('Transaction receipt not found after waiting')
                        } }
                    } else {
                        throw new Error('Connected wallet provider cannot send transactions programmatically')
                    }
                } catch (fbErr) {
                    if (import.meta.env.DEV) console.error('[BscUsdtPayment] Fallback eth_sendTransaction failed', fbErr)
                    throw fbErr
                }
            }
        } catch (txErr) {
            if (txErr.code === 4001 || txErr.code === 'ACTION_REJECTED') {
                throw new Error('Transaction rejected by user.')
            }
            // Try to extract a readable reason
            const reason = txErr?.reason || txErr?.error?.message || txErr?.message
            throw new Error(reason || 'Transaction failed')
        }

        txHash.value = tx.hash
        step.value   = 'broadcasting'

        await tx.wait(1)

        step.value = 'verifying'
        await new Promise(resolve => setTimeout(resolve, 3000))

        const verifyRes = await axios.post('/user/bsc/verify-transaction', {
            trx:     depositTrx.value,
            tx_hash: txHash.value,
        })

        if (!verifyRes.data.success) {
            throw new Error(verifyRes.data.message || 'Verification failed')
        }

        step.value           = 'success'
        successMessage.value = verifyRes.data.message || 'Your USDT deposit has been credited to your account.'
        emit('depositSuccess', { trx: depositTrx.value, txHash: txHash.value })

    } catch (err) {
        const validationErrors = err?.response?.data?.errors
        const validationMessage = err?.response?.data?.message
        const msg = validationMessage || err.message || ''

        if (err?.response?.status === 422 && validationErrors) {
            const firstError = Object.values(validationErrors).flat().find(Boolean)
            setError(firstError || validationMessage || 'Deposit request failed validation.')
            return
        }

        if (err.code === 4001 || msg.includes('rejected')) {
            setError('Transaction was rejected. Your funds are safe.')
        } else if (msg.includes('Insufficient USDT')) {
            // Balance issue — clear helpful error
            setError(msg)
        } else if (msg.includes('wrong network') || msg.includes('Chain ID')) {
            // Network issue
            setError(msg)
        } else if (msg.includes('Unable to read USDT balance') || msg.includes('Unable to verify')) {
            // Contract/network communication issue
            setError(msg)
        } else {
            setError(msg || 'Payment failed. Please contact support.')
        }
    }
}
</script>

<style scoped>
/* ── Shell ── */
.bsc-wrap { display:flex; flex-direction:column; gap:16px; }

/* ── Header ── */
.bsc-head { display:flex; align-items:center; gap:12px; }
.bsc-head-icon {
    width:46px; height:46px; border-radius:13px;
    background:rgba(240,185,11,0.12); border:1px solid rgba(240,185,11,0.3);
    display:flex; align-items:center; justify-content:center; flex-shrink:0;
    box-shadow:0 0 16px rgba(240,185,11,0.2);
}
.bsc-head-title { font-size:14px; font-weight:800; color:#fff; margin:0 0 6px; letter-spacing:-0.01em; }
.bsc-badges    { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.bsc-badge {
    font-size:10px; font-weight:700; padding:3px 8px; border-radius:6px;
    text-transform:uppercase; letter-spacing:0.07em;
}
.bsc-badge--yellow { background:rgba(240,185,11,0.14); color:#F0B90B; border:1px solid rgba(240,185,11,0.3); }
.bsc-badge--blue   { background:rgba(96,165,250,0.12); color:#60a5fa; border:1px solid rgba(96,165,250,0.25); }
.bsc-badge--green  { background:rgba(16,185,129,0.12); color:#10b981; border:1px solid rgba(16,185,129,0.25); }

/* ── Notice ── */
.bsc-notice     { padding:12px 14px; border-radius:11px; background:rgba(240,185,11,0.04); border:1px solid rgba(240,185,11,0.16); display:flex; flex-direction:column; gap:10px; }
.bsc-notice-txt { font-size:12px; color:rgba(148,163,184,0.75); margin:0; line-height:1.6; }
.bsc-warn       { display:flex; align-items:flex-start; gap:8px; font-size:11.5px; font-weight:600; color:rgba(253,230,138,0.7); line-height:1.5; }

/* ── Amount section ── */
.bsc-amount-section { display:flex; flex-direction:column; gap:12px; }
.bsc-field     { display:flex; flex-direction:column; gap:7px; }
.bsc-field-lbl { font-size:11.5px; font-weight:700; color:rgba(226,232,240,0.8); }
.bsc-input-wrap { position:relative; display:flex; align-items:center; }
.bsc-input-pfx  {
    position:absolute; left:13px; font-size:13px; font-weight:800;
    color:rgba(148,163,184,0.45); pointer-events:none;
}
.bsc-input {
    width:100%; padding:12px 14px 12px 30px;
    background:rgba(255,255,255,0.04);
    border:1px solid rgba(240,185,11,0.2);
    border-radius:11px; font-size:13.5px; font-weight:600; color:#fff;
    outline:none; transition:border-color 0.2s;
}
.bsc-input:focus { border-color:rgba(240,185,11,0.45); background:rgba(240,185,11,0.03); }
.bsc-input:disabled { opacity:0.5; cursor:not-allowed; }
.bsc-input::placeholder { color:rgba(148,163,184,0.3); }
.bsc-field-hint { font-size:10.5px; color:rgba(148,163,184,0.4); margin:0; }

/* ── Summary ── */
.bsc-summary {
    padding:12px 14px; border-radius:11px;
    background:rgba(240,185,11,0.04); border:1px solid rgba(240,185,11,0.14);
    display:flex; flex-direction:column; gap:8px;
}
.bsc-sum-row       { display:flex; justify-content:space-between; align-items:center; }
.bsc-sum-lbl       { font-size:12px; color:rgba(148,163,184,0.6); }
.bsc-sum-val       { font-size:13px; font-weight:700; color:#e2e8f0; }
.bsc-sum-total     { padding-top:8px; border-top:1px solid rgba(240,185,11,0.12); }

/* ── Action area ── */
.bsc-action-area { display:flex; flex-direction:column; gap:10px; }
.bsc-connected-bar {
    display:flex; align-items:center; justify-content:space-between;
    gap:10px;
    padding:12px 14px; border-radius:11px;
    background:rgba(16,185,129,0.06); border:1px solid rgba(16,185,129,0.2);
}
.bsc-connected-info { display:flex; align-items:center; gap:10px; flex:1; min-width:0; }
.bsc-connected-dot  {
    width:9px; height:9px; border-radius:50%; flex-shrink:0;
    background:#10b981; box-shadow:0 0 7px #10b981;
}
.bsc-connected-addr-wrap { display:flex; flex-direction:column; gap:2px; min-width:0; flex:1; }
.bsc-connected-label { font-size:10px; font-weight:600; color:rgba(16,185,129,0.75); text-transform:uppercase; letter-spacing:0.06em; }
.bsc-connected-addr  { font-size:11.5px; font-weight:700; color:#e2e8f0; font-family:monospace; word-break:break-all; }
.bsc-selected-bar {
    display:flex; align-items:center; justify-content:space-between;
    gap:10px;
    padding:12px 14px; border-radius:11px;
    background:rgba(245,158,11,0.06); border:1px solid rgba(245,158,11,0.2);
}
.bsc-selected-info { display:flex; align-items:center; gap:10px; flex:1; min-width:0; }
.bsc-selected-dot  {
    width:9px; height:9px; border-radius:50%; flex-shrink:0;
    background:#f59e0b; box-shadow:0 0 7px #f59e0b;
}
.bsc-selected-addr-wrap { display:flex; flex-direction:column; gap:4px; min-width:0; flex:1; }
.bsc-selected-label { font-size:10px; font-weight:600; color:rgba(245,158,11,0.75); text-transform:uppercase; letter-spacing:0.06em; }
.bsc-selected-addr  { font-size:11.5px; font-weight:700; color:#e2e8f0; font-family:monospace; word-break:break-all; }

/* ── Buttons ── */
.bsc-btn {
    display:inline-flex; align-items:center; justify-content:center; gap:8px;
    padding:13px 22px; border-radius:12px; border:none;
    font-size:13px; font-weight:800; cursor:pointer; transition:all 0.2s;
    letter-spacing:0.01em; width:100%;
}
.bsc-btn--primary {
    background:linear-gradient(135deg,#c88c00,#F0B90B);
    color:#0f172a;
    box-shadow:0 4px 18px rgba(240,185,11,0.35);
}
.bsc-btn--primary:hover:not(:disabled) { transform:translateY(-2px); filter:brightness(1.08); }
.bsc-btn--primary:disabled { opacity:0.45; cursor:not-allowed; }
.bsc-btn--ghost {
    background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.12);
    color:rgba(226,232,240,0.7); width:auto; font-size:12px; padding:9px 18px;
}
.bsc-btn--ghost:hover { background:rgba(255,255,255,0.09); }
.bsc-btn--disconnect {
    background:rgba(239,68,68,0.1); border:1px solid rgba(239,68,68,0.25);
    color:#f87171; width:auto; padding:8px 10px; border-radius:9px;
    flex-shrink:0;
}
.bsc-btn--disconnect:hover { background:rgba(239,68,68,0.18); }
.bsc-btn--pay {
    background:linear-gradient(135deg,#0b4f9e,#1a7de8);
    color:#fff;
    box-shadow:0 4px 18px rgba(26,125,232,0.4);
}
.bsc-btn--pay:hover:not(:disabled) { transform:translateY(-2px); filter:brightness(1.08); }
.bsc-btn--pay:disabled { opacity:0.45; cursor:not-allowed; }

/* ── Status blocks ── */
.bsc-status {
    display:flex; align-items:flex-start; gap:14px;
    padding:16px 18px; border-radius:13px;
    border:1px solid;
}
.bsc-status--info    { background:rgba(96,165,250,0.07); border-color:rgba(96,165,250,0.2); }
.bsc-status--warning { background:rgba(251,191,36,0.07); border-color:rgba(251,191,36,0.22); }
.bsc-status--success { background:rgba(16,185,129,0.07); border-color:rgba(16,185,129,0.25); }
.bsc-status--error   { background:rgba(239,68,68,0.07);  border-color:rgba(239,68,68,0.22); }

.bsc-status-title { font-size:13.5px; font-weight:800; color:#e2e8f0; margin:0 0 4px; }
.bsc-status-sub   { font-size:12px; color:rgba(148,163,184,0.7); margin:0; line-height:1.55; }

.bsc-txhash      { font-size:11px; margin:8px 0 0; color:rgba(148,163,184,0.5); font-family:monospace; }
.bsc-txhash-link { color:#60a5fa; text-decoration:underline; }

/* ── Spinner ── */
.bsc-spin-ring {
    width:28px; height:28px; border-radius:50%; flex-shrink:0;
    border:3px solid rgba(96,165,250,0.2);
    border-top-color:#60a5fa;
    animation:bscSpin 0.8s linear infinite;
}
.bsc-spin-ring--yellow { border-color:rgba(251,191,36,0.2); border-top-color:#fbbf24; }
@keyframes bscSpin { to { transform:rotate(360deg); } }

/* ── Check icon ── */
.bsc-check-icon {
    width:44px; height:44px; border-radius:50%; flex-shrink:0;
    background:rgba(16,185,129,0.14); border:1px solid rgba(16,185,129,0.3);
    display:flex; align-items:center; justify-content:center;
}

/* ── Address book picker ── */
.bsc-picker {
    display: flex; flex-direction: column; gap: 8px;
    padding: 12px 14px; border-radius: 12px;
    background: rgba(167,93,237,0.05); border: 1px solid rgba(167,93,237,0.18);
}
.bsc-picker-head { display: flex; align-items: center; gap: 7px; }
.bsc-picker-title { font-size: 11.5px; font-weight: 700; color: rgba(226,232,240,0.8); }
.bsc-wallet-list { display: flex; flex-direction: column; gap: 6px; }
.bsc-wallet-item {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 12px; border-radius: 10px; cursor: pointer;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07);
    transition: border-color 0.15s, background 0.15s;
}
.bsc-wallet-item:hover { border-color: rgba(167,93,237,0.35); background: rgba(167,93,237,0.05); }
.bsc-wallet-item--active { border-color: rgba(167,93,237,0.5); background: rgba(167,93,237,0.08); }
.bsc-wallet-radio {
    width: 16px; height: 16px; border-radius: 50%; flex-shrink: 0;
    border: 2px solid rgba(167,93,237,0.5);
    display: flex; align-items: center; justify-content: center;
    background: rgba(167,93,237,0.08);
}
.bsc-wallet-item--active .bsc-wallet-radio { border-color: #a75ded; }
.bsc-wallet-radio-dot { width: 8px; height: 8px; border-radius: 50%; background: #a75ded; }
.bsc-wallet-meta { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
.bsc-wallet-label { font-size: 11.5px; font-weight: 700; color: #e2e8f0; }
.bsc-wallet-addr  { font-size: 10.5px; color: rgba(148,163,184,0.6); font-family: monospace; word-break: break-all; }
.bsc-picker-hint { margin: 2px 0 0; font-size: 10.5px; color: rgba(148,163,184,0.45); }
.bsc-picker-link { color: #a75ded; text-decoration: none; display: inline-flex; align-items: center; gap: 4px; }
.bsc-picker-link:hover { text-decoration: underline; }

/* ── No saved wallets nudge ── */
.bsc-no-wallets {
    display: flex; align-items: flex-start; gap: 8px;
    padding: 10px 13px; border-radius: 10px; font-size: 11.5px;
    color: rgba(148,163,184,0.7); line-height: 1.55;
    background: rgba(167,93,237,0.04); border: 1px solid rgba(167,93,237,0.14);
}

/* ── Features ── */
.bsc-features {
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:10px;
    margin-top:4px;
}
@media(min-width:640px) { .bsc-features { grid-template-columns:repeat(4,1fr); } }
.bsc-feat {
    display:flex; align-items:center; gap:8px;
    padding:10px 12px; border-radius:10px;
    background:rgba(255,255,255,0.025); border:1px solid rgba(255,255,255,0.06);
}
.bsc-feat-ic      { width:28px; height:28px; border-radius:8px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.bsc-feat-title   { font-size:11px; font-weight:700; margin:0 0 2px; }
.bsc-feat-sub     { font-size:10px; color:rgba(148,163,184,0.5); margin:0; }
</style>
